import type {BrowserAnalysisReason} from './browserAnalysisFailure';
import ENV from '../../../environment';

export const TELEMETRY_URL = ENV.CHESS_API_URL + '/browser_analysis_event/';
export const BROWSER_SF_TELEMETRY_ENABLED = true;
const ENGINE_VERSION = 'sf18-lite';

export interface BrowserAnalysisEvent {
  event: 'attempt' | 'success' | 'failure' | 'cancelled';
  mode: 'normal' | 'deep';
  gameUuid?: string;
  userId?: number;
  reason?: BrowserAnalysisReason;
  message?: string;
  durationMs?: number;
  positionsTotal?: number;
  positionsDone?: number;
}

function detectEnvironment(): Record<string, unknown> {
  const env: Record<string, unknown> = {engine_version: ENGINE_VERSION};
  try {
    const nav: any = typeof navigator !== 'undefined' ? navigator : {};
    const ua: string = nav.userAgent || '';
    env.wasm_supported = typeof WebAssembly !== 'undefined';
    env.cross_origin_isolated =
      typeof (globalThis as any).crossOriginIsolated === 'boolean'
        ? (globalThis as any).crossOriginIsolated
        : undefined;
    env.hardware_concurrency = nav.hardwareConcurrency;
    env.device_memory = nav.deviceMemory;
    env.mobile = /Mobi|Android|iPhone|iPad/i.test(ua);
    env.device_type = env.mobile ? 'mobile' : 'desktop';
    env.os = /Android/i.test(ua)
      ? 'Android'
      : /iPhone|iPad|iOS/i.test(ua)
      ? 'iOS'
      : /Windows/i.test(ua)
      ? 'Windows'
      : /Mac OS/i.test(ua)
      ? 'macOS'
      : /Linux/i.test(ua)
      ? 'Linux'
      : 'unknown';
    const m = ua.match(/(Chrome|Firefox|Safari|Edg|OPR)\/(\d+)/);
    env.browser = m ? m[1] : 'unknown';
    env.browser_version = m ? m[2] : undefined;
  } catch {
    /* best-effort */
  }
  return env;
}

export function reportBrowserAnalysisEvent(
  evt: BrowserAnalysisEvent,
  username?: string,
): void {
  if (!BROWSER_SF_TELEMETRY_ENABLED) {
    return;
  }
  try {
    const body = JSON.stringify({
      event: evt.event,
      mode: evt.mode,
      game_uuid: evt.gameUuid,
      user_id: evt.userId,
      reason: evt.reason,
      message: evt.message ? String(evt.message).slice(0, 500) : undefined,
      duration_ms: evt.durationMs,
      positions_total: evt.positionsTotal,
      positions_done: evt.positionsDone,
      ...detectEnvironment(),
    });

    const nav: any = typeof navigator !== 'undefined' ? navigator : undefined;
    if (nav && typeof nav.sendBeacon === 'function') {
      const blob = new Blob([body], {type: 'application/json'});
      nav.sendBeacon(TELEMETRY_URL, blob);
      return;
    }
    void fetch(TELEMETRY_URL, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(username ? {'X-Username': username} : {}),
      },
      body,
    }).catch(() => undefined);
  } catch {
    /* telemetry must never throw into analysis */
  }
}
